import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Chip,
  TextField,
  InputAdornment,
  Tabs,
  Tab,
  Divider,
  Alert,
  Button,
  Menu,
  MenuItem,
  Tooltip,
  Badge,
} from '@mui/material';
import {
  History as HistoryIcon,
  Search as SearchIcon,
  Delete as DeleteIcon,
  Star as StarIcon,
  StarBorder as StarBorderIcon,
  MoreVert as MoreVertIcon,
  Calculate as CalculateIcon,
  Vaccines as VaccinesIcon,
  PhotoCamera as PhotoCameraIcon,
  LocalHospital as HospitalIcon,
  TrendingUp as TrendingUpIcon,
  Psychology as PsychologyIcon,
  FilterList as FilterListIcon,
  Download as DownloadIcon,
} from '@mui/icons-material';
import { historyManager, HistoryItem } from '../utils/history';

interface HistoryPanelProps {
  userId: string;
  onItemClick?: (item: HistoryItem) => void;
  onClose?: () => void;
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`history-tabpanel-${index}`}
      aria-labelledby={`history-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ pt: 2 }}>{children}</Box>}
    </div>
  );
}

const HistoryPanel: React.FC<HistoryPanelProps> = ({ userId, onItemClick, onClose }) => {
  const [tabValue, setTabValue] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');
  const [history, setHistory] = useState<HistoryItem[]>([]);
  const [favorites, setFavorites] = useState<HistoryItem[]>([]);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedItem, setSelectedItem] = useState<HistoryItem | null>(null);

  useEffect(() => {
    loadHistory();
  }, [userId, tabValue]);

  const loadHistory = () => {
    if (tabValue === 0) {
      // Все действия
      const allHistory = historyManager.getUserHistory(userId);
      setHistory(allHistory);
    } else if (tabValue === 1) {
      // Избранное
      const favs = historyManager.getFavorites(userId);
      setFavorites(favs);
    }
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const handleSearch = (query: string) => {
    setSearchQuery(query);
    if (query.trim()) {
      const searchResults = historyManager.searchHistory(userId, query);
      setHistory(searchResults);
    } else {
      loadHistory();
    }
  };

  const handleToggleFavorite = (itemId: string) => {
    const isFavorite = historyManager.toggleFavorite(itemId);
    loadHistory();
  };

  const handleDeleteItem = (itemId: string) => {
    historyManager.removeHistoryItem(itemId);
    loadHistory();
    setAnchorEl(null);
  };

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, item: HistoryItem) => {
    setAnchorEl(event.currentTarget);
    setSelectedItem(item);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedItem(null);
  };

  const getActionIcon = (action: HistoryItem['action']) => {
    switch (action) {
      case 'calculation':
        return <CalculateIcon sx={{ color: '#4A90E2' }} />;
      case 'vaccination':
        return <VaccinesIcon sx={{ color: '#50C878' }} />;
      case 'atlas':
        return <PhotoCameraIcon sx={{ color: '#FF6B6B' }} />;
      case 'emergency':
        return <HospitalIcon sx={{ color: '#FF9800' }} />;
      case 'growth':
        return <TrendingUpIcon sx={{ color: '#9C27B0' }} />;
      case 'ai_assistant':
        return <PsychologyIcon sx={{ color: '#607D8B' }} />;
      default:
        return <HistoryIcon sx={{ color: '#666' }} />;
    }
  };

  const getActionLabel = (action: HistoryItem['action']) => {
    switch (action) {
      case 'calculation':
        return 'Расчет доз';
      case 'vaccination':
        return 'Вакцинация';
      case 'atlas':
        return 'Атлас инфекций';
      case 'emergency':
        return 'Неотложная помощь';
      case 'growth':
        return 'Графики роста';
      case 'ai_assistant':
        return 'ИИ-помощник';
      default:
        return 'Действие';
    }
  };

  const formatDate = (date: Date) => {
    const now = new Date();
    const diff = now.getTime() - date.getTime();
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);

    if (minutes < 1) return 'Только что';
    if (minutes < 60) return `${minutes} мин назад`;
    if (hours < 24) return `${hours} ч назад`;
    if (days < 7) return `${days} дн назад`;
    return date.toLocaleDateString('ru-RU');
  };

  const currentItems = tabValue === 0 ? history : favorites;

  return (
    <Card sx={{ 
      height: '100%',
      borderRadius: 3,
      boxShadow: '0 8px 32px rgba(0,0,0,0.1)',
      background: 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)'
    }}>
      <CardContent sx={{ p: 0, height: '100%', display: 'flex', flexDirection: 'column' }}>
        {/* Заголовок */}
        <Box sx={{ 
          p: 3, 
          borderBottom: 1, 
          borderColor: 'divider',
          background: 'linear-gradient(45deg, #4A90E2, #50C878)',
          color: 'white',
          borderRadius: '12px 12px 0 0'
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <HistoryIcon sx={{ fontSize: 28 }} />
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                История действий
              </Typography>
            </Box>
            {onClose && (
              <Button onClick={onClose} sx={{ color: 'white' }}>
                Закрыть
              </Button>
            )}
          </Box>
        </Box>

        {/* Поиск */}
        <Box sx={{ p: 2 }}>
          <TextField
            fullWidth
            placeholder="Поиск в истории..."
            value={searchQuery}
            onChange={(e) => handleSearch(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: '#4A90E2' }} />
                </InputAdornment>
              ),
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: 2,
                backgroundColor: 'white'
              }
            }}
          />
        </Box>

        {/* Табы */}
        <Box sx={{ borderBottom: 1, borderColor: 'divider', px: 2 }}>
          <Tabs value={tabValue} onChange={handleTabChange} centered>
            <Tab 
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography>Все действия</Typography>
                  <Badge badgeContent={history.length} color="primary" />
                </Box>
              } 
            />
            <Tab 
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography>Избранное</Typography>
                  <Badge badgeContent={favorites.length} color="secondary" />
                </Box>
              } 
            />
          </Tabs>
        </Box>

        {/* Список истории */}
        <Box sx={{ flex: 1, overflow: 'auto', p: 2 }}>
          {currentItems.length === 0 ? (
            <Alert severity="info" sx={{ mt: 2 }}>
              {tabValue === 0 ? 'История действий пуста' : 'Нет избранных элементов'}
            </Alert>
          ) : (
            <List>
              {currentItems.map((item, index) => (
                <React.Fragment key={item.id}>
                  <ListItem
                    sx={{
                      borderRadius: 2,
                      mb: 1,
                      backgroundColor: 'white',
                      boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                      cursor: 'pointer',
                      '&:hover': {
                        backgroundColor: '#F8F9FA',
                        transform: 'translateY(-2px)',
                        boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
                      },
                      transition: 'all 0.2s ease'
                    }}
                    onClick={() => onItemClick?.(item)}
                  >
                    <ListItemIcon>
                      {getActionIcon(item.action)}
                    </ListItemIcon>
                    <ListItemText
                      primary={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                            {item.title}
                          </Typography>
                          <Chip
                            label={getActionLabel(item.action)}
                            size="small"
                            sx={{ 
                              backgroundColor: '#E8F4FD', 
                              color: '#4A90E2',
                              fontWeight: 500
                            }}
                          />
                        </Box>
                      }
                      secondary={
                        <Box>
                          <Typography variant="body2" sx={{ color: '#666', mb: 0.5 }}>
                            {item.description}
                          </Typography>
                          <Typography variant="caption" sx={{ color: '#999' }}>
                            {formatDate(item.timestamp)} • {item.section}
                          </Typography>
                        </Box>
                      }
                    />
                    <ListItemSecondaryAction>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Tooltip title={item.isFavorite ? 'Убрать из избранного' : 'Добавить в избранное'}>
                          <IconButton
                            onClick={(e) => {
                              e.stopPropagation();
                              handleToggleFavorite(item.id);
                            }}
                            size="small"
                          >
                            {item.isFavorite ? (
                              <StarIcon sx={{ color: '#FFD700' }} />
                            ) : (
                              <StarBorderIcon sx={{ color: '#666' }} />
                            )}
                          </IconButton>
                        </Tooltip>
                        <IconButton
                          onClick={(e) => {
                            e.stopPropagation();
                            handleMenuOpen(e, item);
                          }}
                          size="small"
                        >
                          <MoreVertIcon />
                        </IconButton>
                      </Box>
                    </ListItemSecondaryAction>
                  </ListItem>
                  {index < currentItems.length - 1 && <Divider sx={{ my: 1 }} />}
                </React.Fragment>
              ))}
            </List>
          )}
        </Box>

        {/* Контекстное меню */}
        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleMenuClose}
        >
          <MenuItem onClick={() => handleDeleteItem(selectedItem?.id || '')}>
            <DeleteIcon sx={{ mr: 1, color: '#F44336' }} />
            Удалить
          </MenuItem>
        </Menu>
      </CardContent>
    </Card>
  );
};

export default HistoryPanel;
